import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  BONUS_QUESTS_MAPS,
  MINOR_QUESTS_MAPS,
  NotQuestStage,
  QuestId,
  QuestShortDetails,
  useAvailableQuestsState,
  useInventoryStoreBase,
  useOverlayTrigger,
  usePageQuestState,
  useQuestCurrentStage,
} from '@store'

import styles from './Quests.module.scss'
import { DotCircle } from './components/DotCircle'
import { QuestCircle } from './components/QuestCircle'
import { SubQuestCircle } from './components/SubQuestCircle'
import {
  getQuestLayoutForPage,
  SUB_QUEST_LAYOUT,
  TAPTAP_QUEST_LAYOUT,
} from './questCircleLayout'
import { QuestViewState } from './types/QuestCircleTypes'
import { NOT_QUEST_KEY_ITEM_ID } from '@shared'

type Props = {
  isAnimated: boolean
}

const COUNT_DOTS = 5

const TAPTAP_QUEST = {
  id: -1,
  slug: 'TapTap',
  image: '/assets/tapTap/imgForSpiral.png',
  isSolved: false,
  isAvailable: true,
  type: 'bonus' as const,
  starsCollected: 0,
  starsTotal: 0
}


const getCircleState = (quest?: QuestShortDetails, nextQuest?: QuestShortDetails) => {
  if (quest?.isAvailable || (nextQuest && quest?.id === nextQuest.id)) {
    return QuestViewState.ShouldOpen
  }

  if (quest?.isSolved) {
    return QuestViewState.Showed
  }

  return QuestViewState.Closed
}

function randomString(length: number) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  return Array.from(
    { length },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join('')
}

const getQuestsForScene = (
  availableQuests: QuestShortDetails[],
  pageIndex: number
): QuestShortDetails[] => {
  const questsPerScene = 3
  const startIndex = pageIndex * questsPerScene
  const endIndex = startIndex + questsPerScene

  const safeQuests =
    availableQuests.filter((item) => item.type === 'major') || []
  const sceneQuests: QuestShortDetails[] = safeQuests.slice(
    startIndex,
    endIndex
  )
  let index = 0

  while (sceneQuests.length < questsPerScene) {
    sceneQuests.push({
      id: sceneQuests.length + index,
      slug: randomString(7),
      image: '',
      isSolved: false,
      isAvailable: false,
      type: 'major',
      starsCollected: 0,
      starsTotal: 0
    })
    index++
  }

  return sceneQuests
}

export const Quests = ({ isAnimated }: Props) => {
  const [visible, setVisible] = useState(false)
  const availableQuests = useAvailableQuestsState()
  const pages = usePageQuestState()
  const { triggerOverlay } = useOverlayTrigger()
  const inventoryItems = useInventoryStoreBase((state) => state.items)
  const { currentStage: notQuestStage } = useQuestCurrentStage(QuestId.NotQuest, pages.current === 0)

  const sceneQuests = useMemo(() => {
    const quests = getQuestsForScene(availableQuests, pages.current)
    return quests
  }, [availableQuests, pages.current])

  const nextQuest = useMemo(()=> {
    const majorQuests = availableQuests.filter(q => q.type === 'major');
    const lastSolvedIndex = majorQuests.findLastIndex(q => q.isSolved);
    return lastSolvedIndex !== -1 ? majorQuests[lastSolvedIndex + 1] : undefined
  },[availableQuests])
  
  const countCompletedMajorQuests = useMemo(() => {
    return availableQuests.filter(
      (item) => item.type === 'major' && item.isSolved && !item.isAvailable
    ).length
  }, [availableQuests])

  const onQuestClick = useCallback(() => {
    triggerOverlay(`spiralQuestClick_${countCompletedMajorQuests}`)
  }, [triggerOverlay, countCompletedMajorQuests])

  useLayoutEffect(() => {
    setTimeout(() => {
      setVisible(true)
    }, 1500)
  }, [])

  const bonusQuest = useMemo(() => {
    const quests = availableQuests.filter(
      (item) =>
        item.type === 'bonus' &&
        (item.isAvailable || item.isSolved) &&
        BONUS_QUESTS_MAPS[pages.current]?.some(
          (quest) => quest.slug === item.slug
        )
    )

    return quests.length > 0 ? quests : null
  }, [availableQuests, pages.current])

  const bonusQuestFiltered = useMemo(() => {
    return bonusQuest?.filter((quest) => {
      if (quest.slug !== QuestId.NotQuest) {
        return true
      }

      return (
        inventoryItems.some((item) => item.itemDefId === NOT_QUEST_KEY_ITEM_ID) ||
        (notQuestStage !== null && notQuestStage !== (NotQuestStage.Lock + 1))
      )
    })
  }, [bonusQuest, inventoryItems, notQuestStage])

  const minorQuests = useMemo(() => {
    const quests = availableQuests.filter(
      (item) =>
        item.type === 'minor' &&
        (item.isAvailable || item.isSolved) &&
        MINOR_QUESTS_MAPS[pages.current]?.some(
          (quest) => quest.slug === item.slug
        )
    )
    return quests
  }, [availableQuests, pages.current])

  const handleTapTapQuestClick = useCallback(() => {
    triggerOverlay('tapTapQuestClick')
  }, [triggerOverlay])

  return (
    <div className={styles.scene}>
      {Array.from({ length: COUNT_DOTS }).map((_, index) => (
        <DotCircle
          key={index}
          dotPosition={((index + 1) % 6) as 1 | 2 | 3 | 4 | 5}
          visible={visible}
          pageIndex={pages.current}
        />
      ))}
      {sceneQuests.map((quest, index) => (
        <QuestCircle
          key={quest.id}
          layoutConfig={getQuestLayoutForPage(pages.current, (index + 1) % 4 || 1)}
          quest={quest}
          visible={visible}
          initialState={getCircleState(quest, nextQuest)}
          isAnimated={isAnimated}
          pageIndex={pages.current}
          onClick={onQuestClick}
        />
      ))}
      {minorQuests &&
        minorQuests.map((quest, index) => (
          <SubQuestCircle
            key={quest.id}
            layoutConfig={SUB_QUEST_LAYOUT[index]}
            quest={quest}
            visible={visible}
            initialState={getCircleState(quest)}
            isAnimated={isAnimated}
            pageIndex={pages.current}
          />
        ))}
      {bonusQuestFiltered &&
        bonusQuestFiltered.length > 0 &&
        bonusQuestFiltered.map((quest, index) => (
          <SubQuestCircle
            key={quest.id}
            layoutConfig={SUB_QUEST_LAYOUT[index]}
            quest={quest}
            visible={visible}
            initialState={getCircleState(quest)}
            isAnimated={isAnimated}
            pageIndex={pages.current}
          />
        ))}
      {TAPTAP_QUEST && TAPTAP_QUEST_LAYOUT[pages.current] && (
        <QuestCircle
          key={TAPTAP_QUEST.id}
          layoutConfig={TAPTAP_QUEST_LAYOUT[pages.current]}
          quest={TAPTAP_QUEST}
          visible
          initialState={QuestViewState.Showed}
          pageIndex={pages.current}
          classNamePicture={{ top: '2px', left: '2px' }}
          onClick={() => {
            handleTapTapQuestClick()
          }}
        />
      )}
    </div>
  )
}
